import { useState } from "react";
import { ChevronDown, ChevronRight, Package } from "lucide-react";

// Birleşik hiyerarşik yetki ağacı — Grup → Modül → (İşlemler + Alt-sekmeler).
// Sunum bileşeni: her toggle'ın nasıl çizileceğini render-prop'lar belirler (rol / kullanıcı modu ortak).
export default function PermissionTree({ tree, query, renderModuleVis, renderPerm, renderSubtab, defaultCollapsed = true, hidePerms = false, alwaysShowModules = false }) {
  const [collapsed, setCollapsed] = useState({});
  const [activeGroup, setActiveGroup] = useState(null);
  const toggle = (k) => setCollapsed((c) => ({ ...c, [k]: k in c ? !c[k] : !defaultCollapsed }));
  const isCol = (k) => (k in collapsed ? collapsed[k] : defaultCollapsed);

  const q = (query || "").trim().toLocaleLowerCase("tr");
  const match = (t) => !q || (t || "").toLocaleLowerCase("tr").includes(q);

  const groups = (tree?.groups || []).map((g) => {
    const modules = g.modules.map((m) => {
      const modMatch = match(m.label);
      const perms = hidePerms ? [] : (m.perms || []).filter((p) => modMatch || match(p.label));
      const subtabs = (m.subtabs || []).filter((s) => modMatch || match(s.label));
      const show = alwaysShowModules ? (modMatch || subtabs.length || !q) : (modMatch || perms.length || subtabs.length);
      return { ...m, _perms: perms, _subtabs: subtabs, _show: show };
    }).filter((m) => m._show);
    return { ...g, _modules: modules };
  }).filter((g) => g._modules.length);

  if (groups.length === 0) {
    return <p className="py-8 text-center text-sm text-slate-400">Aramanızla eşleşen modül/yetki bulunamadı.</p>;
  }

  const groupNames = groups.map((g) => g.group);
  const current = activeGroup && groupNames.includes(activeGroup) ? activeGroup : groupNames[0];
  const shownGroups = q ? groups : groups.filter((g) => g.group === current);

  return (
    <div className="space-y-3" data-testid="permission-tree">
      {!q && groups.length > 1 && (
        <div className="flex flex-wrap gap-1.5 border-b border-slate-200 pb-2.5" data-testid="ptree-group-tabs">
          {groups.map((g) => {
            const on = g.group === current;
            return (
              <button key={g.group} type="button" data-testid={`ptree-grouptab-${g.group}`}
                onClick={() => setActiveGroup(g.group)}
                className={`rounded-full px-3 py-1.5 text-[12px] font-semibold transition-colors ${on ? "bg-brand-green text-white shadow-sm" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
                {g.group}<span className="ml-1 opacity-70">{g._modules.length}</span>
              </button>
            );
          })}
        </div>
      )}
      {shownGroups.map((g) => (
        <div key={g.group}>
          {(q || groups.length === 1) && <p className="overline mb-1.5">{g.group}</p>}
          <div className="space-y-1.5">
            {g._modules.map((m) => {
              const col = isCol(m.key) && !q;
              const kid = (m._perms || []).length + (m._subtabs || []).length;
              const slug = m.key === "/" ? "panel" : m.key.slice(1);
              return (
                <div key={m.key} data-testid={`ptree-mod-${slug}`} className="overflow-hidden rounded-lg bg-white ring-1 ring-inset ring-slate-200">
                  <div className="flex items-center gap-2 px-3 py-2.5">
                    <button type="button" data-testid={`ptree-toggle-${slug}`} onClick={() => toggle(m.key)}
                      className="shrink-0 rounded-md p-0.5 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600" title={col ? "Aç" : "Kapat"}>
                      {kid ? (col ? <ChevronRight className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />) : <Package className="h-3.5 w-3.5 opacity-40" />}
                    </button>
                    <span className="flex-1 truncate text-sm font-bold text-slate-700">{m.label}</span>
                    {renderModuleVis && renderModuleVis(m)}
                  </div>
                  {!col && kid > 0 && (
                    <div className="space-y-3 border-t border-slate-100 bg-slate-50/70 px-3 py-3">
                      {(m._perms || []).length > 0 && (
                        <div>
                          <p className="mb-1.5 text-[10px] font-bold uppercase tracking-widest text-emerald-700/70">İşlemler (yetkiler)</p>
                          <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-2">
                            {m._perms.map((p) => <div key={p.code}>{renderPerm && renderPerm(p, m)}</div>)}
                          </div>
                        </div>
                      )}
                      {(m._subtabs || []).length > 0 && (
                        <div>
                          <p className="mb-1.5 text-[10px] font-bold uppercase tracking-widest text-sky-700/70">Alt-sekmeler (menü içi)</p>
                          <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-2">
                            {m._subtabs.map((s) => <div key={s.key}>{renderSubtab && renderSubtab(s, m)}</div>)}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}
